import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import Home from './pages/Home';
import PuzzlesRoutes from './pages/Puzzles';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import Admin from './pages/Admin';
import { AuthProvider, useAuth } from './context/AuthContext';
import Request from './pages/Request';
import OrderConfirmation from './pages/order-confirmation';
import { SiTiktok } from 'react-icons/si';
import { FaInstagram } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';

const Header: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { items } = useCart();
  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <header className="w-full bg-cream py-8 flex flex-col items-center">
      <div className="w-full flex items-center justify-between px-8 mb-8">
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className={location.pathname === '/' ? 'font-bold text-orange' : 'hover:text-orange'}>Home</Link>
          <Link to="/puzzles" className={location.pathname === '/puzzles' ? 'font-bold text-orange' : 'hover:text-orange'}>Available Courses</Link>
          <Link to="/request" className={location.pathname === '/request' ? 'font-bold text-orange' : 'hover:text-orange'}>Suggest a Course</Link>
          <Link to="/cart" className={location.pathname === '/cart' ? 'font-bold text-orange relative flex items-center' : 'hover:text-orange relative flex items-center'}>
            <span className="text-xl" role="img" aria-label="cart" style={{ fontSize: '1.3rem', marginLeft: 2 }}>🛒</span>
            {cartCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-18px',
                backgroundColor: '#2e7d32',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 10
              }}>
                {cartCount}
              </span>
            )}
          </Link>
          {user && user.role === 'ADMIN' && (
            <Link to="/admin" className={location.pathname === '/admin' ? 'font-bold text-orange' : 'hover:text-orange'}>Admin</Link>
          )}
        </nav>
        {/* Desktop Auth */}
        <div className="hidden md:flex flex-1 justify-end items-center space-x-4">
          {!user ? (
            <>
              <Link to="/login" className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Sign In</Link>
              <Link to="/register" className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Register</Link>
            </>
          ) : (
            <>
              <span className="font-bold text-logo-gray">{user.firstName || user.email}</span>
              <button onClick={logout} className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Logout</button>
            </>
          )}
        </div>
        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col justify-center items-center h-10 w-10 z-20"
          onClick={() => setMenuOpen(true)}
          aria-label="Open navigation menu"
        >
          <span className="block h-0.5 w-6 bg-logo-gray mb-1 transition-all"></span>
          <span className="block h-0.5 w-6 bg-logo-gray mb-1 transition-all"></span>
          <span className="block h-0.5 w-6 bg-logo-gray transition-all"></span>
        </button>
        {/* Mobile Menu Overlay */}
        {menuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex flex-col">
            <div className="bg-cream w-2/3 max-w-xs h-full shadow-lg p-8 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <span className="text-2xl font-bold text-logo-gray flex items-center">
                  <img src={require('./assets/FCP.svg').default} alt="FirstCut Puzzles Logo" className="h-10 w-10 mr-2" />
                  FirstCut Puzzles
                </span>
                <button
                  className="ml-auto text-3xl font-bold text-logo-gray focus:outline-none"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close navigation menu"
                >
                  &times;
                </button>
              </div>
              <nav className="flex flex-col space-y-4">
                <Link to="/" onClick={() => setMenuOpen(false)} className={location.pathname === '/' ? 'font-bold text-orange' : 'hover:text-orange'}>Home</Link>
                <Link to="/puzzles" onClick={() => setMenuOpen(false)} className={location.pathname === '/puzzles' ? 'font-bold text-orange' : 'hover:text-orange'}>Available Courses</Link>
                <Link to="/request" onClick={() => setMenuOpen(false)} className={location.pathname === '/request' ? 'font-bold text-orange' : 'hover:text-orange'}>Suggest a Course</Link>
                <Link to="/cart" onClick={() => setMenuOpen(false)} className={location.pathname === '/cart' ? 'font-bold text-orange relative flex items-center' : 'hover:text-orange relative flex items-center'}>
                  <span className="text-xl" role="img" aria-label="cart" style={{ fontSize: '1.3rem', marginLeft: 2 }}>🛒</span>
                  {cartCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-18px',
                      backgroundColor: '#2e7d32',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 10
                    }}>
                      {cartCount}
                    </span>
                  )}
                </Link>
                {user && user.role === 'ADMIN' && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)} className={location.pathname === '/admin' ? 'font-bold text-orange' : 'hover:text-orange'}>Admin</Link>
                )}
                {!user ? (
                  <>
                    <Link to="/login" onClick={() => setMenuOpen(false)} className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Sign In</Link>
                    <Link to="/register" onClick={() => setMenuOpen(false)} className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Register</Link>
                  </>
                ) : (
                  <>
                    <span className="font-bold text-logo-gray">{user.firstName || user.email}</span>
                    <button onClick={() => { logout(); setMenuOpen(false); }} className="bg-orange text-white px-6 py-2 rounded font-bold hover:bg-logo-gray transition-colors">Logout</button>
                  </>
                )}
              </nav>
            </div>
            <div className="flex-1" onClick={() => setMenuOpen(false)}></div>
          </div>
        )}
      </div>
    </header>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <div className="min-h-screen bg-cream flex flex-col font-serif">
            {/* Promotional Banner */}
            <div className="bg-orange text-white py-2 text-center">
              <p className="text-sm md:text-base">
                💜 Find more cuts at: <a target="_blank" rel="noopener noreferrer" href="https://www.etsy.com/shop/FirstCutPuzzles" className="text-white hover:text-logo-gray">
                firstcutpuzzles.etsy.com</a> 🖤
              </p>
            </div>
            <Header />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/puzzles/*" element={<PuzzlesRoutes />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/request" element={<Request />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </main>
            <footer className="bg-charcoal text-white py-10 mt-12">
              <div className="container mx-auto flex flex-col md:flex-row items-center md:items-start justify-between px-8">
                <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-8">
                  <div>
                    <ul className="space-y-2">
                      <li><Link to="/" className="hover:text-orange">Home</Link></li>
                      <li><Link to="/puzzles" className="hover:text-orange">Available Courses</Link></li>
                      <li><Link to="/request" className="hover:text-orange">Suggest a Course</Link></li>
                      {/* <li><Link to="/contact" className="hover:text-orange">Contact</Link></li> */}
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-2">
                      <li>
                        <a href="https://www.tiktok.com/@firstcut.puzzles" className="hover:text-orange flex items-center" target="_blank" rel="noopener noreferrer">
                          {(SiTiktok as any)({ className: "inline w-5 h-5 mr-2" })} TikTok
                        </a>
                      </li>
                      <li>
                        <a href="https://www.instagram.com/firstcutpuzzles/" className="hover:text-orange flex items-center" target="_blank" rel="noopener noreferrer">
                          {(FaInstagram as any)({ className: "inline w-5 h-5 mr-2" })} Instagram
                        </a>
                      </li>
                      <li>
                        <a href="mailto:admin@firstcutpuzzles.com" className="hover:text-orange flex items-center" target="_blank" rel="noopener noreferrer">
                          {(MdEmail as any)({ className: "inline w-5 h-5 mr-2" })} Contact Us
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-2 text-sm">
                    </ul>
                  </div>
                </div>
              </div>
            </footer>
          </div>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
